import { useEffect, useRef, useState, type JSX } from 'react';

import { isAxiosError } from 'axios';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import {
  OPTIMISTIC_LOCK_EVENT,
  setSuppressOptimisticLockToast,
  type ApiErrorBody,
} from '@/api/axios';
import { ErrorCodes } from '@/constants/errorCodes';
import {
  createJizda,
  deleteJizda,
  getJizda,
  patchJizdaDispatcher,
  patchJizdaDispatcherFields,
  stornoJizda,
  updateJizda,
} from '@/api/services/jizdyService';
import type {
  CarrierType,
  JizdaCreate,
  JizdaResponse,
  JizdaUpdate,
} from '@/api/types/Jizda';
import type { PlaceSummary } from '@/api/types/Place';
import { appendPlaceNote, formatPlaceCopy } from '@/utils/placeNote';
import { BrandChip } from '@/components/shell';
import { DialogSubmitOverlay, SubmitButton } from '@/components/loading';
import { PlaceAutocomplete } from '@/components/registry/PlaceAutocomplete';
import { PlaceRecordCopyButton } from '@/pages/orders/PlaceRecordCopyButton';
import {
  usePlaceRecordCopy,
  type PlaceFieldType,
} from '@/pages/orders/usePlaceRecordCopy';
import { RegistryAutocomplete } from '@/components/registry/RegistryAutocomplete';
import { Roles } from '@/constants/roles';
import { NEW_ORDER_ID } from '@/constants/orders';
import type { MeResponse } from '@/api/types/MeResponse';
import { useUnsavedWork } from '@/auth/unsavedWork';
import { useAppSelector } from '@/store/hooks';
import { selectIsMine, selectUser, selectUserRoles } from '@/store/userSlice';
import { selectAssignedUserName } from '@/store/usersSlice';

const SAVE_BTN_MIN_WIDTH = 140;

// One „copy button + place field" pair. The copy button (user request
// 2026-09-18) takes 44+8 px out of the row, which in the NARROW desktop detail
// panel (CHR-03 — the 1/3 column, not the 900 px modal the prototype shows)
// would squeeze each field to ~128 px. So the pairs are allowed to WRAP: each
// claims a 260 px basis and refuses to go under 240, which keeps the mockup's
// side-by-side row wherever there is room (modal, wide panel) and stacks the
// two pairs — each then full width — where there is not.
//
// Breakpoint-scoped like `TIME_CELL_SX`, and for the same reason: the parent is
// `direction={{ xs: 'column', md: 'row' }}`, so on `xs` the flex axis is
// VERTICAL and a `260px` basis becomes a 260 px HEIGHT — two tall empty blocks
// around the fields (CQE W7). On `xs` each pair therefore takes its content
// height and the full row width; the wrapping basis applies from `md` up, where
// the axis really is horizontal.
const PLACE_ROW_SX = {
  flex: { xs: '1 1 auto', md: '1 1 260px' },
  minWidth: { xs: 0, md: 240 },
  width: { xs: '100%', md: 'auto' },
} as const;


// Shared cell for the CHR-03 two-column detail time grid. Each cell flexes to
// an equal share of the row (so the two columns line up) with a tidy upper
// bound; `minHeight` keeps the touch target ≥44px on the read-only cells too.
// The 200px cap is md-only: on mobile the paired rows collapse to a single
// column (`Stack direction={{ xs:'column', md:'row' }}`), so the cap would
// needlessly narrow each field — most visibly clipping the commodity
// autocomplete name (calendar-polish-3 item 2). Uncapped on `xs`, capped at
// 200 from `md` up so the desktop two-column rows stay aligned (unchanged).
const TIME_CELL_SX = {
  flex: 1,
  maxWidth: { xs: 'none', md: 200 },
  minHeight: 44,
} as const;

// CHR-02 detail form — ONE flat jízda. Foundation editable fields:
// customer / places / loadingDate / plannedArrival / noteForDispatcher +
// self-assign. CHR-02 times (ADR-0037): editable loadingWindowMin + the
// backoffice-only actual arrival/departure; read-only derived plannedDeparture
// ("do") + computed driverDelayMinutes/idleMinutes. The carrier-type tab
// (ADR-0038) is the dispatcher's primary EDITABLE surface — carrierType
// (VAPAS|CONTRACTOR) + řidič (VAPAS only) / SPZ / dopravce (CONTRACTOR). The
// customer note and storno remain DOWNSTREAM cards. The loadings collection
// is gone entirely.
//
// `plannedDeparture` is server-derived (= plannedArrival + loadingWindowMin)
// and NEVER sent back on save — it lives in the form only as a read-only
// display value. `loadingWindowMin` is the canonical, user-editable input.
const DEFAULT_LOADING_WINDOW_MIN = 180; // 3 h (CHR-03 / ADR-0042 default; was 120)

interface OrderFormState {
  customerId?: string;
  customerName: string;
  // Commodity (CHR-04 / ADR-0043). Dual-form like customer: `commodityId`
  // (existing pick) wins over `commodityName` (RULE-05 auto-create). Required
  // at save (inline RHF-style validation surfaced in the body).
  commodityId?: string;
  commodityName: string;
  loadingPlace: string;
  unloadingPlace: string;
  // The Adresář rows the two free-text fields DENOTE, as the server resolved
  // them on the detail (`JizdaResponse.loading/unloadingPlaceRecord`, §E) or as
  // the našeptávač just picked them. DERIVED, never sent back: the jízda saves
  // plain text and nothing else (`performSave`). `null` = free text / a row
  // renamed or removed since — the copy button then falls back to the raw text.
  //
  // They live in the form rather than in the loaded snapshot so a place picked
  // BEFORE saving is copyable immediately — the whole point of keeping the copy
  // in memory (user 2026-09-18).
  loadingPlaceRecord: PlaceSummary | null;
  unloadingPlaceRecord: PlaceSummary | null;
  loadingDate: string;
  plannedArrival: string; // `HH:mm`
  // Read-only display of the server-derived "do". `HH:mm` or '' when null.
  plannedDeparture: string;
  // Canonical, user-editable loading window (minutes). Empty string = unset.
  loadingWindowMin: string;
  actualArrival: string; // `HH:mm`
  actualDeparture: string; // `HH:mm`
  // Unloading-leg times (CHR-03 / ADR-0042) — backoffice-only, `HH:mm`. Mirror
  // the actuals; raw recorded values, no computed metric, no notifications.
  unloadingArrival: string; // `HH:mm`
  unloadingDeparture: string; // `HH:mm`
  // Computed read-only (server). `null` = not-yet-computable (distinct from 0).
  driverDelayMinutes: number | null;
  idleMinutes: number | null;
  // Carrier-type block (ADR-0038, dispatcher-editable). `carrierType` is the
  // own/contractor source of truth. `carrierId`/`carrierName` name ONLY a
  // contractor (VAPAS ⇒ no carrier). `vehicleId`/`vehicleSpz` = the SPZ pool
  // pick (round-trips the canonical value; never re-spaced in the input).
  // `driverName` only applies to VAPAS (cleared on switch to CONTRACTOR).
  carrierType: CarrierType;
  carrierId?: string;
  carrierName: string;
  vehicleId?: string;
  vehicleSpz: string;
  driverName: string;
  noteForDispatcher: string;
  // Detail-only internal/commercial note (zadání §4). Backoffice-editable;
  // dispatcher reads it disabled. Also the storno-reason surface.
  noteForCustomer: string;
  assignedDispatcherSub: string | null;
  version: number;
}

const emptyForm: OrderFormState = {
  customerName: '',
  commodityName: '',
  loadingPlace: '',
  unloadingPlace: '',
  loadingPlaceRecord: null,
  unloadingPlaceRecord: null,
  loadingDate: '',
  plannedArrival: '',
  plannedDeparture: '',
  loadingWindowMin: String(DEFAULT_LOADING_WINDOW_MIN),
  actualArrival: '',
  actualDeparture: '',
  unloadingArrival: '',
  unloadingDeparture: '',
  driverDelayMinutes: null,
  idleMinutes: null,
  carrierType: 'VAPAS',
  carrierId: undefined,
  carrierName: '',
  vehicleId: undefined,
  vehicleSpz: '',
  driverName: '',
  noteForDispatcher: '',
  noteForCustomer: '',
  assignedDispatcherSub: null,
  version: 0,
};

// HTML `<input type="time">` requires `HH:mm` and SILENTLY rejects any other
// format (incl. `HH:mm:ss`). Spring serializes `LocalTime` as `HH:mm:ss` by
// default, so trim seconds on the read path or the input renders empty.
export const normalizeTimeHHmm = (v: string | null | undefined): string =>
  v ? v.slice(0, 5) : '';

// Live client-side mirror of the server-derived "Plánovaný odjezd (do)"
// (CHR-03 item 5 / ADR-0037). Given a `HH:mm` planned arrival and a loading
// window in minutes, returns `HH:mm` = arrival + window (wrapping past
// midnight, matching `LocalTime.plusMinutes`). Returns '' when either input is
// missing/invalid so the read-only field shows the em-dash. The server still
// derives the authoritative value on save; this is preview-only and never
// sent back.
export const computePlannedDeparture = (
  plannedArrivalHHmm: string,
  loadingWindowMin: string,
): string => {
  const arrival = normalizeTimeHHmm(plannedArrivalHHmm);
  if (!/^\d{2}:\d{2}$/.test(arrival)) return '';
  const window = loadingWindowMin.trim();
  if (window === '') return '';
  const windowNum = Number(window);
  if (!Number.isFinite(windowNum) || windowNum < 0) return '';
  const h = Number(arrival.slice(0, 2));
  const m = Number(arrival.slice(3, 5));
  const total = (((h * 60 + m + windowNum) % 1440) + 1440) % 1440;
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};

// Format a computed delay/idle minute count for the read-only chips
// (ADR-0037 null table). `null` = not-yet-computable → em-dash `—` (distinct
// from a computed `0` → `0 min`). `>= 60` → `1 h 30 min` (hours + remainder
// minutes, remainder omitted when 0). `< 60` → `45 min`. Negative idle keeps
// its sign with a true minus glyph: `−30 min`. Czech unit copy via `t`.
const EM_DASH = '—';
const MINUS = '−'; // U+2212 (typographic minus, not hyphen)

export const formatDelayIdleMinutes = (
  minutes: number | null | undefined,
  t: (key: string) => string,
): string => {
  if (minutes === null || minutes === undefined) return EM_DASH;
  const sign = minutes < 0 ? MINUS : '';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const hUnit = t('editor.unit.hour');
  const mUnit = t('editor.unit.minute');
  if (h === 0) return `${sign}${m} ${mUnit}`;
  if (m === 0) return `${sign}${h} ${hUnit}`;
  return `${sign}${h} ${hUnit} ${m} ${mUnit}`;
};

// Optimistic-lock detection on a thrown axios error. The api returns HTTP 409
// with `code === OPTIMISTIC_LOCK` (ADR-0010).
const isOptimisticLockError = (e: unknown): boolean =>
  isAxiosError(e) &&
  e.response?.status === 409 &&
  (e.response?.data as ApiErrorBody | undefined)?.code === ErrorCodes.OPTIMISTIC_LOCK;

// `isNew` controls the loading-window default-fill (CHR-02 MEDIUM fix). For a
// brand-new jízda we default an unset window to 120 (mirrors the ADR-0037
// server default so the input shows the 2 h window). For an EXISTING jízda we
// preserve the server value verbatim — a legitimately-null window stays empty
// so `performSave` sends `null`, never silently materializing 120 (which would
// be a spurious field change / extra Envers revision).
const jizdaToForm = (o: JizdaResponse, isNew = false): OrderFormState => ({
  customerId: o.customer?.id,
  customerName: o.customer?.name ?? '',
  commodityId: o.commodity?.id,
  commodityName: o.commodity?.name ?? '',
  loadingPlace: o.loadingPlace ?? '',
  unloadingPlace: o.unloadingPlace ?? '',
  // `?? null` normalises the api's absent-vs-null (the FE type keeps both
  // optional so older fixtures type-check) into the one shape the form reads.
  loadingPlaceRecord: o.loadingPlaceRecord ?? null,
  unloadingPlaceRecord: o.unloadingPlaceRecord ?? null,
  loadingDate: o.loadingDate ?? '',
  plannedArrival: normalizeTimeHHmm(o.plannedArrival),
  plannedDeparture: normalizeTimeHHmm(o.plannedDeparture),
  loadingWindowMin:
    o.loadingWindowMin != null
      ? String(o.loadingWindowMin)
      : isNew
      ? String(DEFAULT_LOADING_WINDOW_MIN)
      : '',
  actualArrival: normalizeTimeHHmm(o.actualArrival),
  actualDeparture: normalizeTimeHHmm(o.actualDeparture),
  unloadingArrival: normalizeTimeHHmm(o.unloadingArrival),
  unloadingDeparture: normalizeTimeHHmm(o.unloadingDeparture),
  driverDelayMinutes: o.driverDelayMinutes,
  idleMinutes: o.idleMinutes,
  carrierType: o.carrierType,
  carrierId: o.carrier?.id,
  carrierName: o.carrier?.name ?? '',
  vehicleId: o.vehicle?.id,
  vehicleSpz: o.vehicle?.spz ?? '',
  driverName: o.driverName ?? '',
  noteForDispatcher: o.noteForDispatcher ?? '',
  noteForCustomer: o.noteForCustomer ?? '',
  assignedDispatcherSub: o.assignedDispatcherSub,
  version: o.version,
});

// THE editor's single "unsaved edits" signal — `form` diffed field-by-field
// against the loaded snapshot (or against the empty form for a brand-new
// jízda). Re-auth coordinator input (LOCKED plan
// 2026-08-24-auth-session-recovery, W1/F): while this is `true`, a dead session
// must NOT silently redirect through Keycloak — the reload would throw the edits
// away. Exported so tests can pin the semantics.
// The two resolved place records are DERIVED, not edits: they are object
// references (so a `!==` diff against a freshly-built `jizdaToForm` snapshot is
// ALWAYS true — the editor would report itself permanently dirty) and the text
// they mirror is already diffed by `loadingPlace` / `unloadingPlace`.
const DERIVED_FORM_KEYS: readonly (keyof OrderFormState)[] = [
  'loadingPlaceRecord',
  'unloadingPlaceRecord',
];

export const isFormDirty = (
  form: OrderFormState,
  loaded: JizdaResponse | null,
): boolean => {
  const base = loaded === null ? emptyForm : jizdaToForm(loaded);
  return (Object.keys(base) as (keyof OrderFormState)[])
    .filter((key) => !DERIVED_FORM_KEYS.includes(key))
    .some((key) => (form[key] ?? null) !== (base[key] ?? null));
};

// The Safari „prázdné pole ukazuje 12:30" bug (CHR-04 §14, ported) — the
// controlled-empty contract for every native date/time input in this editor:
//   1. `value` is ALWAYS a string, never `undefined` — `normalizeTimeHHmm`
//      maps `null` to `''`, so the field is controlled from first paint and
//      the browser never gets to own its value.
//   2. `autoComplete="off"` on every one of them. Safari's AutoFill is the
//      remaining path by which a visually empty time field can acquire a value
//      the app never set — and a wrong actual time silently saved is worse
//      than a blank one.
//   3. NO `step`/`min`/`max` and NO MUI X picker — both were suspected triggers
//      and were ruled out by the WebKit repro (neither changes the behaviour).
//   4. `data-empty="true"` while the value is `''`. This is the hook for the
//      actual repair, which lives in the theme's `MuiCssBaseline` overrides:
//      WebKit paints an empty date/time control's PLACEHOLDER as a sample value
//      (`12:30`, or today's date) instead of `--:--`, and the rule hides that
//      placeholder — WebKit-only, and only while unfocused. See
//      `web/src/theme/theme.ts` for the full root-cause note.
// `OrderEditor.test.tsx` pins (1), (2) and (4) for all six fields.

// Shared editor body — the `<Stack spacing={3}>` form used by both the modal
// (`variant='modal'`) and the right-side detail panel (`variant='panel'`).
// Pure presentational; all state + handlers live in `OrderEditor`.
//
// RBAC UI-mirror (CHR-02 / ADR-0036 §9 + ADR-0038): a dispatcher may NOT edit
// any foundation field — customer / places / date / plannedArrival / note are
// all `disabled` for a dispatcher (server-enforced too). The dispatcher's
// PRIMARY editable surface is the carrier-type block (carrierType / SPZ /
// dopravce / řidič) — that block is NOT disabled for a dispatcher; it is
// written via `dispatcher-fields` PATCH. Self-assign is the other dispatcher
// write.
interface OrderEditorBodyProps {
  form: OrderFormState;
  setForm: React.Dispatch<React.SetStateAction<OrderFormState>>;
  isNew: boolean;
  isBackoffice: boolean;
  // A soft-deleted jízda is viewable but READ-ONLY (CHR-04 / ADR-0043). Every
  // field is disabled and no write path is reachable. The `viewer` role
  // (CHR-03 §2 / ADR-0046) is the same read-only render app-wide.
  readOnly: boolean;
  // `viewer` (ADR-0046) — distinguishes the role-driven read-only render from
  // the deleted-jízda one so the warning banner + hidden self-assign are
  // viewer-appropriate (a viewer's whole detail is read-only, not "smazaná").
  isViewer: boolean;
  // Whether the required-commodity validation should surface inline (set after
  // a save attempt with an empty commodity). Backoffice/new only.
  showCommodityError: boolean;
  saveError: string | null;
  deleteError: string | null;
  deletePrompt: string | null;
  dispatcherName: string;
  auth: ReturnType<typeof useAuth>;
  me: MeResponse | null;
  onUnassign: () => void;
  onAssignSelf: () => void;
}

const OrderEditorBody = ({
  form,
  setForm,
  isNew,
  isBackoffice,
  readOnly,
  isViewer,
  showCommodityError,
  saveError,
  deleteError,
  deletePrompt,
  dispatcherName,
  auth,
  me,
  onUnassign,
  onAssignSelf,
}: OrderEditorBodyProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  // My-jizdy highlight: the dispatcher chip renders yellow (`mine`) when this
  // jízda is assigned to the logged-in user — single shared selector.
  const isMine = useAppSelector(selectIsMine(form.assignedDispatcherSub));
  // Foundation fields are backoffice-editable, but a deleted jízda is fully
  // read-only for everyone (CHR-04). The commodity is a foundation field.
  const foundationDisabled = !isBackoffice || readOnly;

  // Every place-field write goes through this one pair of handlers so the field
  // TEXT and the resolved RECORD can never drift apart.
  //
  // `onChange` fires on every keystroke AND (just before `onPlaceSelected`) on a
  // selection with the formatted insert text, so the rule is simply: keep the
  // record only while the text still IS its insert form. Edit a character, clear
  // the field, paste something else — the record drops to `null` and the copy
  // button falls back to the raw text with the „nenalezeno" warning, which is
  // the truthful answer: that text no longer denotes that Adresář row.
  const handlePlaceText = (field: PlaceFieldType, text: string): void => {
    setForm((prev) => {
      const record =
        field === 'LOADING' ? prev.loadingPlaceRecord : prev.unloadingPlaceRecord;
      const kept = record !== null && formatPlaceCopy(record) === text ? record : null;
      return field === 'LOADING'
        ? { ...prev, loadingPlace: text, loadingPlaceRecord: kept }
        : { ...prev, unloadingPlace: text, unloadingPlaceRecord: kept };
    });
  };

  // Picking a place from the Adresář stores the WHOLE row (it now carries
  // `contact` — CHR-06 §C) so the copy button works on a fresh pick, before any
  // save, without a lookup. It also carries the place's „Poznámka pro dispečery"
  // into the jízda's „Poznámka pro dispečera" (contract §E — the rule itself
  // lives ONLY in `appendPlaceNote`); THAT half is guarded by `isBackoffice`
  // because the note field is backoffice-only. The record itself is stored
  // unconditionally — it is read-only data, not a write.
  const handlePlaceSelected = (field: PlaceFieldType, place: PlaceSummary): void => {
    setForm((prev) => {
      const withRecord =
        field === 'LOADING'
          ? { ...prev, loadingPlaceRecord: place }
          : { ...prev, unloadingPlaceRecord: place };
      return isBackoffice
        ? {
            ...withRecord,
            noteForDispatcher: appendPlaceNote(prev.noteForDispatcher, place),
          }
        : withRecord;
    });
  };

  // „Vykopírovat celý řádek z Adresáře" for each place field (user request
  // 2026-09-18). Deliberately NOT gated on `foundationDisabled`: copying is
  // read-only, and the dispatcher — who sees these fields disabled — is exactly
  // who needs the SMS text without hopping to the Adresář and searching.
  //
  // Purely in-memory: both the text and the record come straight out of `form`,
  // which the detail response already filled. No request is issued by a click.
  const { copiedField, copyRecord } = usePlaceRecordCopy();
  const handleCopyRecord = (field: PlaceFieldType): void => {
    void copyRecord(
      field,
      field === 'LOADING' ? form.loadingPlace : form.unloadingPlace,
      field === 'LOADING' ? form.loadingPlaceRecord : form.unloadingPlaceRecord,
    );
  };
  return (
    <Stack spacing={3}>
      {readOnly ? (
        <Alert
          severity={isViewer ? 'info' : 'warning'}
          data-testid={isViewer ? 'viewer-readonly-alert' : 'deleted-readonly-alert'}
        >
          {isViewer ? t('editor.viewerReadOnly') : t('editor.deletedReadOnly')}
        </Alert>
      ) : null}
      {saveError ? (
        <Alert severity="error">{t('editor.saveFailed')}</Alert>
      ) : null}
      {deleteError ? (
        <Alert severity="error">{t('editor.deleteFailed')}</Alert>
      ) : null}
      {deletePrompt ? (
        <Alert severity="warning" data-testid="delete-reason-prompt">
          {deletePrompt}
        </Alert>
      ) : null}

      {/* Flat jízda foundation fields */}
      <RegistryAutocomplete
        kind="customer"
        label={t('editor.field.customer')}
        value={
          form.customerName
            ? { id: form.customerId, name: form.customerName }
            : null
        }
        onChange={(v) =>
          setForm((prev) => ({
            ...prev,
            customerId: v?.id,
            customerName: v?.name ?? '',
          }))
        }
        disabled={foundationDisabled}
      />
      {/* CHR-03 detail-time layout (ADR-0042, refined CHR-03 follow-up). The
          time fields stack in tidy aligned rows:
            Row 1: Datum nakládky (alone, on its own row)
            Row 2: Plánovaný příjezd (od) | Okno nakládky | Plánovaný odjezd (do, live preview)
            Row 3: Skutečný příjezd | Skutečný odjezd
            Row 4: Příjezd na vykládku | Odjezd z vykládky
            Row 5: Zpoždění řidiče | Prostoj
          The two-column rows (3+) share `TIME_CELL_SX` (equal width) so the
          columns line up; the three planned fields (Row 2) share the same cell
          width and all three are `size="small"` TextFields so they sit on one
          baseline. "Do" is server-derived (read-only TextField, not disabled)
          but mirrored live client-side from plannedArrival + loadingWindowMin
          (ADR-0037 unchanged). For a dispatcher (!isBackoffice) the read-only
          "do" value text renders greyed (text.disabled) to match the
          surrounding disabled inputs. Window + actuals + unloading times are
          backoffice-only; delay/idle are computed read-only chips. */}
      {/* `flex-start` only on desktop (md row) so the commodity cell — which can
          grow taller via its inline required-error caption — top-aligns with the
          datum field without stretching it. On mobile the row is a single column;
          `stretch` lets each field fill the full width (paired with the md-only
          `TIME_CELL_SX` cap) so the commodity autocomplete name is not clipped
          (calendar-polish-3 item 2). */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={2}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
      >
        <TextField
          type="date"
          label={t('editor.field.loadingDate')}
          value={form.loadingDate}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, loadingDate: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          inputProps={{ 'data-empty': form.loadingDate === '' ? 'true' : undefined }}
          size="small"
          sx={TIME_CELL_SX}
          disabled={foundationDisabled}
        />
        {/* Komodita (CHR-04 / ADR-0043) — RIGHT of Datum nakládky on the same
            row (post-CHR-03 the datum sits alone, so it pairs with commodity).
            Required (inline error after a failed save). Backoffice-editable;
            disabled for a dispatcher or a deleted/read-only jízda. */}
        <Box sx={{ ...TIME_CELL_SX, display: 'flex', flexDirection: 'column' }}>
          <RegistryAutocomplete
            kind="commodity"
            label={t('editor.field.commodity')}
            required
            value={
              form.commodityName
                ? { id: form.commodityId, name: form.commodityName }
                : null
            }
            onChange={(v) =>
              setForm((prev) => ({
                ...prev,
                commodityId: v?.id,
                commodityName: v?.name ?? '',
              }))
            }
            disabled={foundationDisabled}
          />
          {showCommodityError ? (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.error.main, mt: 0.5 }}
              data-testid="commodity-required-error"
            >
              {t('editor.commodityRequired')}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} flexWrap="wrap">
        <TextField
          type="time"
          label={t('editor.field.plannedArrival')}
          value={form.plannedArrival}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, plannedArrival: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          inputProps={{ 'data-empty': form.plannedArrival === '' ? 'true' : undefined }}
          size="small"
          sx={TIME_CELL_SX}
          disabled={foundationDisabled}
          data-testid="field-planned-arrival"
        />
        <TextField
          type="number"
          label={t('editor.field.window')}
          value={form.loadingWindowMin}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, loadingWindowMin: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: 1, inputMode: 'numeric' }}
          size="small"
          sx={{ ...TIME_CELL_SX, '& input': { fontVariantNumeric: 'tabular-nums' } }}
          disabled={foundationDisabled}
          data-testid="field-loading-window"
        />
        {/* Plánovaný odjezd (do) — read-only, live client-side preview =
            plannedArrival + loadingWindowMin (CHR-03 item 5). Rendered as a
            read-only TextField that mirrors the two sibling inputs' geometry
            exactly (size="small", shrink label) so the three Row-2 cells share
            one baseline — fixes the misalignment of the former custom Stack
            cell. It is NOT `disabled` (that would grey it for backoffice too);
            it is `readOnly`/derived for BOTH roles. For a dispatcher
            (!isBackoffice) the value text is greyed (text.disabled) to match
            the surrounding disabled inputs. */}
        <TextField
          label={t('editor.field.plannedDeparture')}
          value={
            computePlannedDeparture(
              form.plannedArrival,
              form.loadingWindowMin,
            ) || EM_DASH
          }
          InputLabelProps={{ shrink: true }}
          InputProps={{ readOnly: true }}
          size="small"
          sx={{
            ...TIME_CELL_SX,
            '& .MuiInputBase-input': {
              fontVariantNumeric: 'tabular-nums',
              ...(isBackoffice
                ? {}
                : {
                    color: theme.palette.text.disabled,
                    WebkitTextFillColor: theme.palette.text.disabled,
                  }),
            },
          }}
          data-testid="value-planned-departure"
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <TextField
          type="time"
          label={t('editor.field.actualArrival')}
          value={form.actualArrival}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, actualArrival: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          inputProps={{ 'data-empty': form.actualArrival === '' ? 'true' : undefined }}
          size="small"
          sx={TIME_CELL_SX}
          disabled={foundationDisabled}
          data-testid="field-actual-arrival"
        />
        <TextField
          type="time"
          label={t('editor.field.actualDeparture')}
          value={form.actualDeparture}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, actualDeparture: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          inputProps={{ 'data-empty': form.actualDeparture === '' ? 'true' : undefined }}
          size="small"
          sx={TIME_CELL_SX}
          disabled={foundationDisabled}
          data-testid="field-actual-departure"
        />
      </Stack>

      {/* Unloading-leg times (CHR-03 item 7 / ADR-0042) — backoffice-only,
          aligned under the actuals; raw recorded, no computed metric, no
          notifications. Dispatcher reads them disabled. */}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <TextField
          type="time"
          label={t('editor.field.unloadingArrival')}
          value={form.unloadingArrival}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, unloadingArrival: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          inputProps={{ 'data-empty': form.unloadingArrival === '' ? 'true' : undefined }}
          size="small"
          sx={TIME_CELL_SX}
          disabled={foundationDisabled}
          data-testid="field-unloading-arrival"
        />
        <TextField
          type="time"
          label={t('editor.field.unloadingDeparture')}
          value={form.unloadingDeparture}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, unloadingDeparture: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          inputProps={{ 'data-empty': form.unloadingDeparture === '' ? 'true' : undefined }}
          size="small"
          sx={TIME_CELL_SX}
          disabled={foundationDisabled}
          data-testid="field-unloading-departure"
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <Stack spacing={0.5} sx={TIME_CELL_SX}>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {t('editor.field.driverDelay')}
          </Typography>
          <Box
            component="span"
            sx={{
              alignSelf: 'flex-start',
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
              bgcolor: theme.palette.background.default,
              color: theme.palette.text.primary,
            }}
            data-testid="chip-driver-delay"
          >
            {formatDelayIdleMinutes(form.driverDelayMinutes, t)}
          </Box>
        </Stack>
        <Stack spacing={0.5} sx={TIME_CELL_SX}>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {t('editor.field.idle')}
          </Typography>
          <Box
            component="span"
            sx={{
              alignSelf: 'flex-start',
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
              bgcolor: theme.palette.background.default,
              color: theme.palette.text.primary,
            }}
            data-testid="chip-idle"
          >
            {formatDelayIdleMinutes(form.idleMinutes, t)}
          </Box>
        </Stack>
      </Stack>

      {/* Nakládka / Vykládka (CHR-06 §8, `prototyp.html#s-editor-ac`) — the only
          changed row in the editor: the plain text fields became Adresář
          našeptávače. The fields stay FREE TEXT (no FK, no migration); picking
          a place merely fills „Název — Adresa" and — for backoffice — appends
          that place's note to „Poznámka pro dispečera". Nakládka offers
          LOADING + Obojí, Vykládka UNLOADING + Obojí (server-side, inclusive).
          Neither field can create a place: the Adresář list is its only
          source. */}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ flexWrap: 'wrap' }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={PLACE_ROW_SX}
          data-testid="place-row-loading"
        >
          <PlaceRecordCopyButton
            field="LOADING"
            text={form.loadingPlace}
            copied={copiedField === 'LOADING'}
            onCopy={handleCopyRecord}
            data-testid="place-copy-loading"
          />
          <PlaceAutocomplete
            label={t('editor.field.loadingPlace')}
            value={form.loadingPlace}
            onChange={(text) => handlePlaceText('LOADING', text)}
            onPlaceSelected={(place) => handlePlaceSelected('LOADING', place)}
            placeType="LOADING"
            disabled={foundationDisabled}
            data-testid="place-autocomplete-loading"
          />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={PLACE_ROW_SX}
          data-testid="place-row-unloading"
        >
          <PlaceRecordCopyButton
            field="UNLOADING"
            text={form.unloadingPlace}
            copied={copiedField === 'UNLOADING'}
            onCopy={handleCopyRecord}
            data-testid="place-copy-unloading"
          />
          <PlaceAutocomplete
            label={t('editor.field.unloadingPlace')}
            value={form.unloadingPlace}
            onChange={(text) => handlePlaceText('UNLOADING', text)}
            onPlaceSelected={(place) => handlePlaceSelected('UNLOADING', place)}
            placeType="UNLOADING"
            disabled={foundationDisabled}
            data-testid="place-autocomplete-unloading"
          />
        </Stack>
      </Stack>
      {/* Poznámka pro dispečera (overview note) — backoffice-only (zadání §4). */}
      <TextField
        label={t('editor.field.noteForDispatcher')}
        value={form.noteForDispatcher}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, noteForDispatcher: e.target.value }))
        }
        size="small"
        multiline
        minRows={3}
        fullWidth
        disabled={!isBackoffice}
        data-testid="field-note-dispatcher"
      />

      {/* Poznámka pro zákazníka (detail-only internal/commercial note, zadání
          §4) — backoffice-editable, dispatcher reads it disabled. Mirrors the
          dispatcher-note field's styling/multiline. Same target as the storno
          reason dialog (both edit noteForCustomer). */}
      <TextField
        label={t('editor.field.noteForCustomer')}
        value={form.noteForCustomer}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, noteForCustomer: e.target.value }))
        }
        size="small"
        multiline
        minRows={3}
        fullWidth
        disabled={!isBackoffice}
        data-testid="field-note-customer"
      />

      {/* Carrier-type block (ADR-0038) — dispatcher-EDITABLE surface.
          ToggleButtonGroup VAPAS|CONTRACTOR; switching clears the now-
          irrelevant field (client mirror, server authoritative). VAPAS →
          řidič + SPZ; CONTRACTOR → SPZ + dopravce (no řidič). */}
      <Stack spacing={1.5} data-testid="carrier-type-block">
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary }}
        >
          {t('editor.field.carrierType')}
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={form.carrierType}
          onChange={(_, next: CarrierType | null) => {
            if (next === null || next === form.carrierType) return;
            setForm((prev) =>
              next === 'VAPAS'
                ? {
                    // VAPAS clears the contractor (no carrier FK on own fleet).
                    ...prev,
                    carrierType: 'VAPAS',
                    carrierId: undefined,
                    carrierName: '',
                  }
                : {
                    // CONTRACTOR clears the driver (foreign driver irrelevant).
                    ...prev,
                    carrierType: 'CONTRACTOR',
                    driverName: '',
                  },
            );
          }}
          size="small"
          disabled={readOnly}
          sx={{
            alignSelf: 'flex-start',
            '& .MuiToggleButton-root': {
              minHeight: 44,
              px: 2.5,
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <ToggleButton value="VAPAS" data-testid="carrier-type-vapas">
            {t('carrierType.VAPAS')}
          </ToggleButton>
          <ToggleButton value="CONTRACTOR" data-testid="carrier-type-contractor">
            {t('carrierType.CONTRACTOR')}
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
          {/* Řidič — VAPAS only (zadání §5). */}
          {form.carrierType === 'VAPAS' ? (
            <TextField
              label={t('editor.field.driverName')}
              value={form.driverName}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, driverName: e.target.value }))
              }
              size="small"
              fullWidth
              disabled={readOnly}
              data-testid="field-driver-name"
            />
          ) : null}
          {/* SPZ — both types. RegistryAutocomplete round-trips canonical;
              NEVER re-spaced here (re-format is read-only display only). */}
          <RegistryAutocomplete
            kind="vehicle"
            label={t('editor.field.spz')}
            value={
              form.vehicleSpz
                ? { id: form.vehicleId, name: form.vehicleSpz }
                : null
            }
            onChange={(v) =>
              setForm((prev) => ({
                ...prev,
                vehicleId: v?.id,
                vehicleSpz: v?.name ?? '',
              }))
            }
            disabled={readOnly}
            inputSx={{ minWidth: 160 }}
          />
        </Stack>
        {/* Dopravce — CONTRACTOR only (zadání §5). */}
        {form.carrierType === 'CONTRACTOR' ? (
          <RegistryAutocomplete
            kind="carrier"
            label={t('editor.field.carrier')}
            value={
              form.carrierName
                ? { id: form.carrierId, name: form.carrierName }
                : null
            }
            onChange={(v) =>
              setForm((prev) => ({
                ...prev,
                carrierId: v?.id,
                carrierName: v?.name ?? '',
              }))
            }
            disabled={readOnly}
          />
        ) : null}
      </Stack>

      {/* Dispatcher row — self-assign allowed for any authenticated user
          (backoffice may self-assign without the dispatcher role). */}
      {!isNew ? (
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: theme.palette.text.primary,
              minWidth: 100,
            }}
          >
            {t('editor.field.dispatcher')}
          </Typography>
          {form.assignedDispatcherSub ? (
            <Tooltip title={dispatcherName} placement="top">
              <span>
                <BrandChip
                  variant={isMine ? 'mine' : 'brand'}
                  dot
                  maxWidth={180}
                  fontSize={13}
                >
                  {dispatcherName}
                </BrandChip>
              </span>
            </Tooltip>
          ) : (
            <BrandChip variant="danger" dot fontSize={13}>
              {t('editor.dispatcher.unassigned')}
            </BrandChip>
          )}
          {/* Self-assign / unassign — hidden entirely for a `viewer`
              (ADR-0046: no write affordance), shown for any other
              authenticated user. */}
          {auth.isAuthenticated && me && !isViewer ? (
            form.assignedDispatcherSub === me.sub ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onUnassign()}
                disabled={readOnly}
              >
                {t('editor.dispatcher.unassignSelf')}
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onAssignSelf()}
                disabled={readOnly}
              >
                {form.assignedDispatcherSub
                  ? t('editor.dispatcher.overrideSelf')
                  : t('editor.dispatcher.assignSelf')}
              </Button>
            )
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

export interface OrderEditorProps {
  // `null` → editor closed. `'__new__'` → new jízda. Any other string →
  // existing jízda id to fetch + edit.
  orderId: string | null;
  onClose: (didSave?: boolean) => void;
  onSaved: (order: JizdaResponse) => void;
  onDeleted: (orderId: string) => void;
  variant?: 'modal' | 'panel';
}

export const OrderEditor = ({
  orderId,
  onClose,
  onSaved,
  onDeleted,
  variant = 'modal',
}: OrderEditorProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const auth = useAuth();
  const me = useAppSelector(selectUser);
  const myRoles = useAppSelector(selectUserRoles);
  const isBackoffice = myRoles.includes(Roles.BACKOFFICE);
  // `viewer` (CHR-03 §2 / ADR-0046) — app-wide read-only. The detail renders
  // exactly like the deleted-jízda read-only path: every field disabled, no
  // save/delete/storno, no self-assign. Server-enforced too (writes 403).
  const isViewer = myRoles.includes(Roles.VIEWER);

  const [form, setForm] = useState<OrderFormState>(emptyForm);
  // Snapshot of the loaded jízda for the dispatcher diff.
  const [loadedOrder, setLoadedOrder] = useState<JizdaResponse | null>(null);
  // App-wide unsaved-work registry (`@/auth/unsavedWork`): the re-auth
  // coordinator asks before redirecting while this editor holds edits.
  // Unregisters on unmount / once the form matches the saved snapshot again.
  useUnsavedWork(isFormDirty(form, loadedOrder));
  const dispatcherName = useAppSelector(
    selectAssignedUserName(form.assignedDispatcherSub),
  );
  const [loadStatus, setLoadStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState<string | null>(null);
  const suppressLockDialogRef = useRef(false);
  // Storno (CHR-02 / ADR-0039) state. The storno-direction dialog is shown for a
  // backoffice user; `stornoReason` is the editable `noteForCustomer` pre-filled
  // from the loaded jízda. `unstorno` distinguishes the restore path (no reason
  // prompt — un-storno keeps `noteForCustomer` intact server-side).
  const [showStornoDialog, setShowStornoDialog] = useState(false);
  const [showUnstornoConfirm, setShowUnstornoConfirm] = useState(false);
  const [stornoReason, setStornoReason] = useState('');
  const [stornoing, setStornoing] = useState(false);
  const [stornoError, setStornoError] = useState<string | null>(null);

  const isNew = orderId === NEW_ORDER_ID;
  const isStorno = loadedOrder?.stornoState === 'STORNO';
  // A soft-deleted jízda's detail now loads (CHR-04 / ADR-0043) but is fully
  // read-only: no field editable, no save/delete/storno. The server is the
  // authoritative source (`loadedOrder.deleted`); a brand-new jízda is never
  // deleted.
  const isDeleted = loadedOrder?.deleted === true;
  // A `viewer` (ADR-0046) makes the WHOLE editor read-only regardless of the
  // jízda's deleted state — same disabled-fields + no-write-paths render.
  const readOnly = isDeleted || isViewer;
  // Inline required-commodity validation surfaced after a failed save attempt.
  const [showCommodityError, setShowCommodityError] = useState(false);

  // Fetch on open (existing jízda). Reset to empty on new.
  useEffect(() => {
    if (orderId === null) return;
    if (isNew) {
      setForm(emptyForm);
      setLoadedOrder(null);
      setLoadStatus('ready');
      return;
    }
    let alive = true;
    setLoadStatus('loading');
    getJizda(orderId)
      .then((o) => {
        if (!alive) return;
        setForm(jizdaToForm(o));
        setLoadedOrder(o);
        setLoadStatus('ready');
      })
      .catch(() => {
        if (!alive) return;
        setLoadStatus('error');
      });
    return () => {
      alive = false;
    };
  }, [orderId, isNew]);

  // Listen for optimistic-lock events from the axios interceptor.
  useEffect(() => {
    if (orderId === null) return;
    const handler = (): void => {
      if (suppressLockDialogRef.current) return;
      setShowLockDialog(true);
    };
    window.addEventListener(OPTIMISTIC_LOCK_EVENT, handler);
    return () => window.removeEventListener(OPTIMISTIC_LOCK_EVENT, handler);
  }, [orderId]);

  // Dispatcher save path (ADR-0038). A dispatcher's editable surface is the
  // carrier-type block (carrierType / SPZ / dopravce / řidič) plus self-assign.
  // We diff each against the loaded snapshot and issue the scoped PATCHes:
  //   - carrier fields → `dispatcher-fields` (carrierId/carrierName,
  //     carrierType, vehicleId/vehicleSpz, driverName);
  //   - dispatcher assignment → `dispatcher` (self-assign/unassign).
  // Both are version-explicit and chained (each adopts the prior's new
  // version). `noteForDispatcher` + foundation fields are backoffice-only.
  const handleDispatcherSave = async (
    current: OrderFormState,
  ): Promise<JizdaResponse> => {
    if (orderId === null || isNew || loadedOrder === null) {
      throw new Error('dispatcher save requires an existing loaded jízda');
    }
    let currentVersion = current.version || loadedOrder.version;

    // 1) Carrier-type block diff. Server enforces coherence (VAPAS clears
    //    carrier, CONTRACTOR clears driverName); we send the user's intent.
    const carrierFieldsChanged =
      current.carrierType !== loadedOrder.carrierType ||
      (current.carrierId ?? null) !== (loadedOrder.carrier?.id ?? null) ||
      (current.carrierName.trim() || null) !==
        (loadedOrder.carrier?.name ?? null) ||
      (current.vehicleId ?? null) !== (loadedOrder.vehicle?.id ?? null) ||
      (current.vehicleSpz.trim() || null) !==
        (loadedOrder.vehicle?.spz ?? null) ||
      (current.driverName.trim() || null) !== (loadedOrder.driverName ?? null);
    if (carrierFieldsChanged) {
      const after = await patchJizdaDispatcherFields(orderId, {
        version: currentVersion,
        carrierType: current.carrierType,
        // Map RegistryValue.id → carrierId; typed-but-unpersisted → carrierName
        // (RULE-05 auto-create). VAPAS sends nulls; server coherence agrees.
        carrierId: current.carrierType === 'VAPAS' ? null : current.carrierId ?? null,
        carrierName:
          current.carrierType === 'VAPAS'
            ? null
            : current.carrierName.trim() || null,
        vehicleId: current.vehicleId ?? null,
        vehicleSpz: current.vehicleSpz.trim() || null,
        driverName:
          current.carrierType === 'VAPAS'
            ? current.driverName.trim() || null
            : null,
      });
      currentVersion = after.version;
    }

    // 2) Dispatcher self-assign / unassign diff.
    const newDispatcher = current.assignedDispatcherSub;
    const dispatcherChanged =
      newDispatcher !== (loadedOrder.assignedDispatcherSub ?? null);
    if (dispatcherChanged) {
      const after = await patchJizdaDispatcher(orderId, {
        version: currentVersion,
        dispatcherSub: newDispatcher,
      });
      currentVersion = after.version;
    }
    return getJizda(orderId);
  };

  // Core save — role-aware, version-explicit. Reads the *passed* form so the
  // 409 retry can run against a freshly-merged snapshot.
  const performSave = async (current: OrderFormState): Promise<JizdaResponse> => {
    if (!isNew && !isBackoffice) {
      return handleDispatcherSave(current);
    }
    // `loadingWindowMin` is canonical; `plannedDeparture` is server-derived
    // (ADR-0037) so it is deliberately NOT sent. `actualArrival`/
    // `actualDeparture` are backoffice-only writes; the dispatcher path never
    // reaches here (it returns via `handleDispatcherSave` above).
    const windowMin = current.loadingWindowMin.trim();
    const base = {
      customerId: current.customerId,
      customerName: current.customerName.trim() || null,
      // Commodity (CHR-04 / ADR-0043) — id wins over name (like customer); the
      // api auto-creates from `commodityName` (RULE-05) and rejects (400
      // VALIDATION) when both are absent.
      commodityId: current.commodityId,
      commodityName: current.commodityName.trim() || null,
      // Plain text ONLY — `loadingPlaceRecord` / `unloadingPlaceRecord` are
      // read-side derivations (§E: no FK from a jízda to a place) and are
      // deliberately absent from this payload. The server re-resolves them on
      // the response, which is where `jizdaToForm` picks them back up.
      loadingPlace: current.loadingPlace.trim() || null,
      unloadingPlace: current.unloadingPlace.trim() || null,
      loadingDate: current.loadingDate || null,
      plannedArrival: normalizeTimeHHmm(current.plannedArrival) || null,
      loadingWindowMin: windowMin === '' ? null : Number(windowMin),
      actualArrival: normalizeTimeHHmm(current.actualArrival) || null,
      actualDeparture: normalizeTimeHHmm(current.actualDeparture) || null,
      // Unloading-leg times (CHR-03 / ADR-0042) — backoffice-only, sent on the
      // PUT/POST path only (the dispatcher path returns via
      // `handleDispatcherSave` and never carries them). No notifications.
      unloadingArrival: normalizeTimeHHmm(current.unloadingArrival) || null,
      unloadingDeparture: normalizeTimeHHmm(current.unloadingDeparture) || null,
      // Carrier-type block (ADR-0038). VAPAS sends null carrier; server
      // coherence agrees (and clears the foreign driver for CONTRACTOR).
      carrierType: current.carrierType,
      carrierId: current.carrierType === 'VAPAS' ? null : current.carrierId ?? null,
      carrierName:
        current.carrierType === 'VAPAS'
          ? null
          : current.carrierName.trim() || null,
      vehicleId: current.vehicleId ?? null,
      vehicleSpz: current.vehicleSpz.trim() || null,
      driverName:
        current.carrierType === 'VAPAS'
          ? current.driverName.trim() || null
          : null,
      noteForDispatcher: current.noteForDispatcher.trim() || null,
      // Detail-only customer note (zadání §4). Sent on the backoffice
      // PUT/POST path only — never on the dispatcher path (which returns via
      // `handleDispatcherSave`), matching the dispatcher-note field.
      noteForCustomer: current.noteForCustomer.trim() || null,
      assignedDispatcherSub: current.assignedDispatcherSub,
    };
    if (isNew) {
      return createJizda(base as JizdaCreate);
    }
    const payload: JizdaUpdate = { version: current.version, ...base };
    const updated = await updateJizda(orderId as string, payload);
    return getJizda(updated.id);
  };

  // Merge the user's pending edits on top of a fresh server snapshot, adopting
  // the server's version — makes a stale-version 409 recoverable without
  // clobbering the user's in-progress edits.
  const mergeUserEditsOntoFresh = (
    user: OrderFormState,
    fresh: JizdaResponse,
  ): OrderFormState => ({
    ...user,
    version: fresh.version,
  });

  const handleSave = async (): Promise<void> => {
    if (submitting) return;
    if (readOnly) return; // a deleted jízda is never writable from the UI
    // Required-commodity validation (CHR-04 / ADR-0043) — surfaced inline,
    // pre-empting the api's 400 VALIDATION. The rule lives on the backoffice
    // PUT/POST path only; the dispatcher field-patch path never carries
    // commodity, so skip the check there (matches the api write-boundary).
    const onBackofficeWritePath = isNew || isBackoffice;
    if (onBackofficeWritePath) {
      const hasCommodity = Boolean(
        form.commodityId || form.commodityName.trim(),
      );
      if (!hasCommodity) {
        setShowCommodityError(true);
        return;
      }
      setShowCommodityError(false);
    }
    setSaveError(null);
    setSubmitting(true);
    suppressLockDialogRef.current = true;
    setSuppressOptimisticLockToast(true);
    try {
      let saved: JizdaResponse;
      try {
        saved = await performSave(form);
      } catch (e) {
        if (!isOptimisticLockError(e) || orderId === null || isNew) throw e;
        const fresh = await getJizda(orderId);
        const merged = mergeUserEditsOntoFresh(form, fresh);
        setForm(merged);
        setLoadedOrder(fresh);
        saved = await performSave(merged);
      }
      setForm(jizdaToForm(saved));
      setLoadedOrder(saved);
      onSaved(saved);
      onClose(true);
    } catch (e) {
      if (isOptimisticLockError(e)) {
        setShowLockDialog(true);
      } else {
        setSaveError(e instanceof Error ? e.message : 'unknown');
      }
    } finally {
      suppressLockDialogRef.current = false;
      setSuppressOptimisticLockToast(false);
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (): void => {
    if (form.noteForDispatcher.trim().length === 0) {
      // RULE / ADR-0008 — UI gate. A delete reason is recorded in the note.
      setDeletePrompt(t('editor.deleteReasonRequired'));
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (deleting) return;
    setDeleteError(null);
    if (orderId === null || isNew) return;
    setDeleting(true);
    try {
      await deleteJizda(orderId, form.version, form.noteForDispatcher.trim());
      onDeleted(orderId);
      setShowDeleteConfirm(false);
      onClose();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'unknown');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleAssignSelf = async (): Promise<void> => {
    if (orderId === null || isNew || !me?.sub) return;
    const isAssignedToSomeoneElse =
      form.assignedDispatcherSub !== null && form.assignedDispatcherSub !== me.sub;
    if (isAssignedToSomeoneElse) {
      setShowOverrideConfirm(true);
      return;
    }
    await doAssign(me.sub);
  };

  const handleUnassign = async (): Promise<void> => {
    if (orderId === null || isNew) return;
    await doAssign(null);
  };

  const doAssign = async (sub: string | null): Promise<void> => {
    if (orderId === null || isNew) return;
    try {
      const updated = await patchJizdaDispatcher(orderId, {
        version: form.version,
        dispatcherSub: sub,
      });
      setForm(jizdaToForm(updated));
      setLoadedOrder(updated);
      onSaved(updated);
      setShowOverrideConfirm(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'unknown');
    }
  };

  const handleReload = async (): Promise<void> => {
    if (orderId === null || isNew) return;
    try {
      const fresh = await getJizda(orderId);
      setForm(jizdaToForm(fresh));
      setLoadedOrder(fresh);
      setShowLockDialog(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'unknown');
    }
  };

  // Storno (CHR-02 / ADR-0039). "Stornovat" opens the reason-prompt dialog with
  // the customer note pre-filled (editable); confirming POSTs `stornoState:
  // 'STORNO'` with the edited `noteForCustomer`. "Zrušit storno" un-storno
  // restores via `stornoState: 'ACTIVE'` (no reason prompt). Both refresh the
  // form + propagate the new state to the list via `onSaved`.
  const openStornoDialog = (): void => {
    if (loadedOrder === null) return;
    setStornoError(null);
    setStornoReason(loadedOrder.noteForCustomer ?? '');
    setShowStornoDialog(true);
  };

  const applyStorno = async (
    stornoState: 'STORNO' | 'ACTIVE',
    noteForCustomer?: string | null,
  ): Promise<JizdaResponse | null> => {
    if (orderId === null || isNew || loadedOrder === null) return null;
    setStornoError(null);
    setStornoing(true);
    try {
      const updated = await stornoJizda(orderId, {
        version: form.version || loadedOrder.version,
        stornoState,
        noteForCustomer,
      });
      setForm(jizdaToForm(updated));
      setLoadedOrder(updated);
      onSaved(updated);
      return updated;
    } catch (e) {
      setStornoError(e instanceof Error ? e.message : 'unknown');
      return null;
    } finally {
      setStornoing(false);
    }
  };

  const handleStornoConfirm = async (): Promise<void> => {
    if (stornoing) return;
    const updated = await applyStorno('STORNO', stornoReason.trim() || null);
    if (updated) setShowStornoDialog(false);
  };

  const handleUnstornoConfirm = async (): Promise<void> => {
    if (stornoing) return;
    const updated = await applyStorno('ACTIVE');
    if (updated) setShowUnstornoConfirm(false);
  };

  if (orderId === null) {
    // Closed. Modal: MUI Dialog renders nothing when `open` is false-y; panel:
    // OrderDetailPanel owns the empty-state, so render nothing here.
    if (variant === 'panel') return <></>;
    return <Dialog open={false} onClose={() => onClose()} />;
  }

  const titleStr = isNew
    ? t('editor.titleNew')
    : form.customerName
    ? t('editor.titleEdit', { customer: form.customerName })
    : t('editor.titleEditNoCustomer');

  const bodyEl =
    loadStatus === 'loading' ? (
      <Typography>{t('list.loadingMore')}</Typography>
    ) : loadStatus === 'error' ? (
      <Alert severity="error">{t('editor.loadFailed')}</Alert>
    ) : (
      <OrderEditorBody
        form={form}
        setForm={setForm}
        isNew={isNew}
        isBackoffice={isBackoffice}
        readOnly={readOnly}
        isViewer={isViewer}
        showCommodityError={showCommodityError}
        saveError={saveError}
        deleteError={deleteError}
        deletePrompt={deletePrompt}
        dispatcherName={dispatcherName}
        auth={auth}
        me={me}
        onUnassign={() => void handleUnassign()}
        onAssignSelf={() => void handleAssignSelf()}
      />
    );

  // Shared footer (delete + storno + cancel + save). Storno/un-storno is
  // backoffice-only (ADR-0039 §9) — a dispatcher sees the state/chip but gets no
  // action button. The button toggles label by current state.
  const footerEl = (
    <>
      {!isNew && isBackoffice && !readOnly ? (
        <Stack direction="row" gap={1}>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteClick}
            data-testid="delete-button"
          >
            {t('editor.delete')}
          </Button>
          {isStorno ? (
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                setStornoError(null);
                setShowUnstornoConfirm(true);
              }}
              data-testid="unstorno-button"
            >
              {t('editor.storno.unstorno')}
            </Button>
          ) : (
            <Button
              color="error"
              variant="outlined"
              onClick={openStornoDialog}
              data-testid="storno-button"
            >
              {t('editor.storno.storno')}
            </Button>
          )}
        </Stack>
      ) : (
        <span />
      )}
      <Stack direction="row" gap={1}>
        <Button onClick={() => onClose()} variant="outlined">
          {readOnly ? t('editor.close') : t('editor.cancel')}
        </Button>
        {/* Save (ADR-0038): backoffice/new saves all fields via PUT/POST; a
            dispatcher saves the carrier-type block via the scoped
            `dispatcher-fields` PATCH (routed in `handleDispatcherSave`). Both
            need the main Save, so it shows for any editable state. A deleted
            jízda (CHR-04 / ADR-0043) is read-only → no Save. */}
        {readOnly ? null : (
        <SubmitButton
          loading={submitting}
          onClick={() => void handleSave()}
          variant="contained"
          sx={{ minWidth: SAVE_BTN_MIN_WIDTH }}
          data-testid="save-button"
        >
          {isNew
            ? t('editor.save')
            : t('editor.saveWithVersion', { version: form.version })}
        </SubmitButton>
        )}
      </Stack>
    </>
  );

  // Auxiliary dialogs are always modal.
  const auxDialogs = (
    <>
      {/* Delete confirm */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        data-testid="delete-confirm"
      >
        <DialogTitle>{t('editor.deleteConfirmTitle')}</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={deleting} />
          <Typography variant="body2">
            {t('editor.deleteConfirmBody', { reason: form.noteForDispatcher })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
            {t('editor.deleteConfirmNo')}
          </Button>
          <SubmitButton
            color="error"
            loading={deleting}
            onClick={() => void handleDeleteConfirm()}
          >
            {t('editor.deleteConfirmYes')}
          </SubmitButton>
        </DialogActions>
      </Dialog>

      {/* Override-reassignment confirm */}
      <Dialog
        open={showOverrideConfirm}
        onClose={() => setShowOverrideConfirm(false)}
        data-testid="override-confirm"
      >
        <DialogTitle>{t('editor.dispatcher.overrideConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('editor.dispatcher.overrideConfirmBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOverrideConfirm(false)}>
            {t('editor.dispatcher.overrideConfirmNo')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => void doAssign(me?.sub ?? null)}
          >
            {t('editor.dispatcher.overrideConfirmYes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Storno reason-prompt dialog (CHR-02 / ADR-0039). Pre-filled, editable
          customer note. Confirm → POST /storno {stornoState:'STORNO'}. */}
      <Dialog
        open={showStornoDialog}
        onClose={() => setShowStornoDialog(false)}
        maxWidth="sm"
        fullWidth
        data-testid="storno-dialog"
      >
        <DialogTitle>{t('editor.storno.dialogTitle')}</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={stornoing} />
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('editor.storno.dialogBody')}
            </Typography>
            {stornoError ? (
              <Alert severity="error">{t('editor.storno.failed')}</Alert>
            ) : null}
            <TextField
              label={t('editor.storno.reasonLabel')}
              value={stornoReason}
              onChange={(e) => setStornoReason(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              autoFocus
              data-testid="storno-reason-field"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStornoDialog(false)} disabled={stornoing}>
            {t('editor.storno.dialogCancel')}
          </Button>
          <SubmitButton
            color="error"
            loading={stornoing}
            onClick={() => void handleStornoConfirm()}
            data-testid="storno-confirm"
          >
            {t('editor.storno.dialogConfirm')}
          </SubmitButton>
        </DialogActions>
      </Dialog>

      {/* Un-storno confirm (CHR-02 / ADR-0039). Restores ACTIVE; keeps the
          customer note intact server-side (no reason prompt). */}
      <Dialog
        open={showUnstornoConfirm}
        onClose={() => setShowUnstornoConfirm(false)}
        data-testid="unstorno-confirm"
      >
        <DialogTitle>{t('editor.storno.unstornoTitle')}</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={stornoing} />
          {stornoError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('editor.storno.failed')}
            </Alert>
          ) : null}
          <Typography variant="body2">
            {t('editor.storno.unstornoBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnstornoConfirm(false)} disabled={stornoing}>
            {t('editor.storno.unstornoCancel')}
          </Button>
          <SubmitButton
            color="primary"
            loading={stornoing}
            onClick={() => void handleUnstornoConfirm()}
            data-testid="unstorno-confirm-button"
          >
            {t('editor.storno.unstornoConfirm')}
          </SubmitButton>
        </DialogActions>
      </Dialog>

      {/* Optimistic-lock dialog */}
      <Dialog
        open={showLockDialog}
        onClose={() => setShowLockDialog(false)}
        data-testid="optimistic-lock-dialog"
      >
        <DialogTitle>{t('optimisticLock.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('optimisticLock.body')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLockDialog(false)}>
            {t('optimisticLock.discard')}
          </Button>
          <Button variant="contained" onClick={() => void handleReload()}>
            {t('optimisticLock.reload')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // ----- Panel variant (ADR-0033, desktop right detail) -------------------
  if (variant === 'panel') {
    return (
      <>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
          data-testid="order-editor-panel"
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              position: 'relative',
              p: theme.spacing(2),
            }}
          >
            <DialogSubmitOverlay submitting={submitting} />
            {bodyEl}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: theme.spacing(1),
              p: theme.spacing(1.5, 2),
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            {footerEl}
          </Box>
        </Box>
        {auxDialogs}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={true}
        onClose={() => onClose()}
        maxWidth="md"
        fullWidth
        data-testid="order-editor"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="span" sx={{ flex: 1, fontSize: 17, fontWeight: 600 }}>
            {titleStr}
          </Box>
          {/* Smazáno badge (CHR-04 / ADR-0043) — mirrors the panel header; a
              soft-deleted jízda opened via the mobile/modal path is read-only.
              Gated on `isDeleted` (NOT `readOnly`): a `viewer` (ADR-0046) is
              read-only too but the jízda is NOT deleted — showing "SMAZÁNO"
              for a viewer would be a false state. The viewer-readonly Alert in
              the body carries the read-only signal for that role. */}
          {isDeleted ? (
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                bgcolor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
              }}
              data-testid="order-editor-deleted-badge"
            >
              {t('panel.deletedBadge')}
            </Box>
          ) : null}
          {!isNew ? (
            <Typography
              variant="caption"
              sx={{
                fontFamily: theme.monospaceFontFamily,
                color: theme.palette.text.secondary,
              }}
            >
              #{(orderId as string).slice(-8)}
            </Typography>
          ) : null}
          <IconButton onClick={() => onClose()} aria-label={t('editor.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={submitting} />
          {bodyEl}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          {footerEl}
        </DialogActions>
      </Dialog>
      {auxDialogs}
    </>
  );
};
